/**
 * Tick interpolation (R2).
 *
 * The simulation advances at 30 Hz; the display runs faster. Rendering
 * interpolates between the previous and current fixed-point transforms at
 * `ctx.time.alpha` and **never mutates simulation state**.
 *
 * Interpolating in float is correct here. Floats are forbidden in the
 * simulation, not on the render side, and lerping fixed-point values in
 * integers would quantize motion back to whole sub-units for no benefit.
 */

import type { Entities } from '../sim/entities.js'
import { MAX_ENTITIES } from '../core/config.js'

const INV_ONE = 1 / 65536

/**
 * Double-buffered per-entity transforms.
 *
 * The previous tick's values must be captured **before** the simulation
 * advances, not reconstructed afterwards: velocity is not a reliable inverse
 * once a unit has been teleported, repathed, or destroyed and recycled.
 */
export class TransformBuffer {
  private readonly prevX: Float32Array
  private readonly prevY: Float32Array
  private readonly prevHeading: Float32Array
  private readonly prevTurret: Float32Array
  private readonly currX: Float32Array
  private readonly currY: Float32Array
  private readonly currHeading: Float32Array
  private readonly currTurret: Float32Array
  /** Generation at capture, so a recycled slot does not interpolate across lives. */
  private readonly generation: Uint16Array

  constructor(capacity = MAX_ENTITIES) {
    this.prevX = new Float32Array(capacity)
    this.prevY = new Float32Array(capacity)
    this.prevHeading = new Float32Array(capacity)
    this.prevTurret = new Float32Array(capacity)
    this.currX = new Float32Array(capacity)
    this.currY = new Float32Array(capacity)
    this.currHeading = new Float32Array(capacity)
    this.currTurret = new Float32Array(capacity)
    this.generation = new Uint16Array(capacity)
  }

  /** Capture the simulation's current transforms as the new "current". */
  capture(entities: Entities): void {
    const limit = entities.limit
    for (let s = 0; s < limit; s++) {
      const gen = entities.generation[s]
      const alive = entities.isAlive(s)
      if (!alive) continue

      if (this.generation[s] !== gen) {
        // A fresh entity in this slot: snap rather than interpolate from
        // whatever the previous occupant was doing.
        this.generation[s] = gen
        this.prevX[s] = entities.x[s] * INV_ONE
        this.prevY[s] = entities.y[s] * INV_ONE
        this.prevHeading[s] = entities.heading[s]
        this.prevTurret[s] = entities.turret[s]
      } else {
        this.prevX[s] = this.currX[s]
        this.prevY[s] = this.currY[s]
        this.prevHeading[s] = this.currHeading[s]
        this.prevTurret[s] = this.currTurret[s]
      }
      this.currX[s] = entities.x[s] * INV_ONE
      this.currY[s] = entities.y[s] * INV_ONE
      this.currHeading[s] = entities.heading[s]
      this.currTurret[s] = entities.turret[s]
    }
  }

  /** Interpolated world X for a slot. `alpha` of 0 is the previous tick. */
  x(slot: number, alpha: number): number {
    return this.prevX[slot] + (this.currX[slot] - this.prevX[slot]) * alpha
  }

  y(slot: number, alpha: number): number {
    return this.prevY[slot] + (this.currY[slot] - this.prevY[slot]) * alpha
  }

  /** Interpolated heading in radians, taking the short way round the circle. */
  heading(slot: number, alpha: number): number {
    return bamToRadians(lerpAngle(this.prevHeading[slot], this.currHeading[slot], alpha))
  }

  turret(slot: number, alpha: number): number {
    return bamToRadians(lerpAngle(this.prevTurret[slot], this.currTurret[slot], alpha))
  }
}

/**
 * Interpolate BAM16 angles the short way round. Lerping 65000 → 100 linearly
 * spins a turret most of the way round the circle instead of nudging it.
 */
export function lerpAngle(from: number, to: number, alpha: number): number {
  let d = (to - from) % 65536
  if (d > 32768) d -= 65536
  else if (d < -32768) d += 65536
  return from + d * alpha
}

export function bamToRadians(bam: number): number {
  return (bam / 65536) * Math.PI * 2
}

export function radiansToBam(rad: number): number {
  return Math.round((rad / (Math.PI * 2)) * 65536) & 0xffff
}
